import { apiInterfaceForJson, apiInterfaceForFormData } from "../common/apiManager";
import { serviceConstants } from "../common/constants";
import { getUserAuthToken } from "../common/util";

export const fetchConversations = () => {
    return apiInterfaceForJson("/ai-chat/v1/conversations", "GET");
};

export const createConversation = (title) => {
    return apiInterfaceForJson("/ai-chat/v1/conversations", "POST", { title });
};

export const renameConversation = (id, title) => {
    return apiInterfaceForJson(`/ai-chat/v1/conversations/${id}`, "PATCH", { title });
};

export const deleteConversation = (id) => {
    return apiInterfaceForJson(`/ai-chat/v1/conversations/${id}`, "DELETE");
};

export const fetchMessages = (id) => {
    return apiInterfaceForJson(`/ai-chat/v1/conversations/${id}/messages`, "GET");
};

export const fetchUsage = () => {
    return apiInterfaceForJson("/ai-chat/v1/usage", "GET");
};

export const uploadAttachments = (conversationId, fileList) => {
    const formData = new FormData();
    Array.from(fileList).forEach((file) => formData.append("files", file));
    return apiInterfaceForFormData(`/ai-chat/v1/conversations/${conversationId}/upload`, "POST", formData);
};

export const getAttachmentUrl = (conversationId, attachmentId) => {
    const token = getUserAuthToken();
    // Note: this URL is for <img src=...> use. Auth via token query param fallback,
    // but our auth middleware uses Bearer header — so for img display we fetch as blob.
    return `${serviceConstants.API_HOST_NAME}/ai-chat/v1/conversations/${conversationId}/attachments/${attachmentId}`;
};

/**
 * Fetch an attachment as a blob URL (for inline image display with auth).
 * Returns a Promise<string> (object URL). Caller should URL.revokeObjectURL when unmounted.
 */
export const fetchAttachmentBlob = async (conversationId, attachmentId) => {
    const token = getUserAuthToken();
    const url = `${serviceConstants.API_HOST_NAME}/ai-chat/v1/conversations/${conversationId}/attachments/${attachmentId}`;
    const res = await fetch(url, {
        headers: { Authorization: "Bearer " + token }
    });
    if (!res.ok) throw new Error("Failed to load attachment");
    const blob = await res.blob();
    return URL.createObjectURL(blob);
};

/**
 * Streams an assistant reply via SSE.
 * @param {string} conversationId
 * @param {string} message
 * @param {object} options - { attachments: Array }
 * @param {object} handlers - { onToken(text), onDone(meta), onError(message) }
 * @returns {AbortController} - call .abort() to cancel
 */
export const streamMessage = (conversationId, message, options = {}, handlers = {}) => {
    const controller = new AbortController();
    const token = getUserAuthToken();
    const url = `${serviceConstants.API_HOST_NAME}/ai-chat/v1/conversations/${conversationId}/messages`;
    const attachments = options.attachments || [];

    (async () => {
        try {
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + token
                },
                body: JSON.stringify({ message, attachments }),
                signal: controller.signal
            });

            if (!response.ok) {
                let errMsg = `Request failed (${response.status})`;
                try {
                    const body = await response.json();
                    errMsg = body?.message || errMsg;
                } catch (_) { /* ignore */ }
                handlers.onError?.(errMsg);
                return;
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });

                let sepIdx;
                while ((sepIdx = buffer.indexOf("\n\n")) !== -1) {
                    const rawEvent = buffer.slice(0, sepIdx);
                    buffer = buffer.slice(sepIdx + 2);

                    let eventName = "message";
                    let dataLine = "";
                    rawEvent.split("\n").forEach((line) => {
                        if (line.startsWith("event:")) eventName = line.slice(6).trim();
                        else if (line.startsWith("data:")) dataLine += line.slice(5).trim();
                    });

                    if (!dataLine) continue;
                    let payload;
                    try { payload = JSON.parse(dataLine); } catch { continue; }

                    if (eventName === "token") handlers.onToken?.(payload.content || "");
                    else if (eventName === "status") handlers.onStatus?.(payload.message || "");
                    else if (eventName === "done") handlers.onDone?.(payload);
                    else if (eventName === "error") handlers.onError?.(payload.message || "Stream error");
                }
            }
        } catch (err) {
            if (err.name === "AbortError") return;
            handlers.onError?.(err.message || "Stream failed");
        }
    })();

    return controller;
};
